import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import {firstValueFrom} from 'rxjs';
import {TranslocoPipe} from '@jsverse/transloco';
import {ToastService} from '@frontend/toast';
import {
  AttributeConsistencyResponse,
  AuditInfo,
  EvaluationSubCriteriaImportanceScore,
  TenderPhaseType,
} from '@frontend/shared-api';
import {BadgeListComponent} from '../list/badge-list/badge-list.component';
import {ImportanceScoreSelectorComponent} from '../importance-score-selector/importance-score-selector.component';
import {ProjectStepSaveButtonComponent} from '../../project-step-save-button/project-step-save-button.component';
import {AuditInfoComponent} from '../audit-info/audit-info.component';
import {AttributeConsistencyStatusComponent} from '../attribute-consistency-status/attribute-consistency-status.component';
import {AttributeConsistencyDialogComponent} from '../attribute-consistency-dialog/attribute-consistency-dialog.component';
import {AttributeConsistencyResolutionRequest} from '../attribute-consistency-dialog/attribute-consistency-dialog.types';
import {AttributeConsistencyService} from '../../services/attribute-consistency.service';
import {
  AttributeConsistencyChange,
  AttributeScoreSelection,
  AttributeScoreVM,
  SubCriteriaPanelVM,
} from './sub-criteria-tab.types';

@Component({
  selector: 'app-sub-criteria-tab',
  imports: [
    TranslocoPipe,
    BadgeListComponent,
    ImportanceScoreSelectorComponent,
    ProjectStepSaveButtonComponent,
    AuditInfoComponent,
    AttributeConsistencyStatusComponent,
    AttributeConsistencyDialogComponent,
  ],
  templateUrl: './sub-criteria-tab.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SubCriteriaTabComponent {
  private readonly consistencyService = inject(AttributeConsistencyService);
  private readonly toast = inject(ToastService);

  readonly subCriteriaPanel = input.required<SubCriteriaPanelVM>();
  readonly disabled = input.required<boolean>();
  readonly projectId = input.required<number>();
  readonly phase = input.required<TenderPhaseType>();
  readonly showConsistency = input(false);
  readonly lastSaveAuditInfo = input<AuditInfo | null>(null);

  readonly scoreSelected = output<AttributeScoreSelection>();
  readonly save = output<void>();
  readonly consistencyChanged = output<AttributeConsistencyChange>();

  protected readonly openedAttribute = signal<AttributeScoreVM | undefined>(undefined);
  protected readonly savingConsistency = signal(false);

  readonly hasAttributes = computed<boolean>(() => {
    return this.subCriteriaPanel().badges.length > 0;
  });

  readonly isSaving = computed<boolean>(() => {
    return this.subCriteriaPanel().isSaving;
  });

  readonly isScoreSelectionDisabled = computed<boolean>(() => {
    return this.subCriteriaPanel().isSaving || this.disabled();
  });

  readonly canSave = computed<boolean>(() => {
    return (
      this.subCriteriaPanel().hasUnsavedScores &&
      this.subCriteriaPanel().isSaveAllowed &&
      !this.disabled()
    );
  });

  protected readonly isConsistencyDialogVisible = computed<boolean>(
    () => this.openedAttribute() !== undefined,
  );

  onScoreSelected(
    directusId: string,
    score: EvaluationSubCriteriaImportanceScore | undefined,
  ): void {
    this.scoreSelected.emit({directusId: directusId, score: score});
  }

  onSave(): void {
    this.save.emit();
  }

  onConsistencyDetailsRequested(attribute: AttributeScoreVM): void {
    this.openedAttribute.set(attribute);
  }

  onConsistencyDialogVisibleChange(visible: boolean): void {
    if (!visible) {
      this.openedAttribute.set(undefined);
    }
  }

  async onConsistencyResolved(request: AttributeConsistencyResolutionRequest): Promise<void> {
    const attribute = this.openedAttribute();

    if (attribute?.id === undefined) {
      return;
    }

    this.savingConsistency.set(true);

    try {
      const response: AttributeConsistencyResponse = await firstValueFrom(
        this.consistencyService.upsert(
          this.projectId(),
          this.subCriteriaPanel().evaluationCriteriaId,
          this.subCriteriaPanel().id,
          attribute.id,
          {resolution: request.resolution, comment: request.comment},
          this.phase(),
        ),
      );

      this.toast.showSuccess('common.success', 'evaluation.consistency.resolutionSaved');
      this.consistencyChanged.emit({
        subCriteriaId: this.subCriteriaPanel().id,
        attributeDirectusId: attribute.directusId,
        consistency: response.consistency,
      });
      this.openedAttribute.set(undefined);
    } finally {
      this.savingConsistency.set(false);
    }
  }
}
